// src/components/parent-dashboard/api/middleApiService.js

/**
 * API Service for Middle Stage (Grades 6-8) Parent Dashboard
 * Contains specialized endpoints for middle stage
 * Matching the backend FastAPI endpoints in parent_middle.py
 */

import { API_BASE_URL } from "../../../config";

// Enum values matching the backend
const ParentResponse = {
    YES: "Yes",
    SOMETIMES: "Sometimes",
    NO: "No",
    NOT_SURE: "Not sure"
};

const HomeResource = {
    BOOKS_MAGAZINES: "Books and Magazines",
    NEWSPAPERS: "Newspapers",
    TOYS_GAMES_SPORTS: "Toys, Games and sports",
    PHONE_COMPUTER: "Phone and Computer",
    INTERNET: "Internet",
    PUBLIC_BROADCAST: "Public Broadcast System",
    CWSN_RESOURCES: "Resources for CWSN"
};

const SchoolSupportArea = {
    LANGUAGES: "Languages (R1, R2, R3)",
    MATHEMATICS: "Mathematics",
    SCIENCE: "Science",
    SOCIAL_SCIENCE: "Social Science",
    SELF_BELIEF_RELIANCE: "Building self-belief & self-reliance",
    SOCIAL_SKILLS_CONFLICT: "Developing social skills & conflict resolution",
    DIFFICULT_EMOTIONS_ANGER: "Managing difficult emotions like anger",
    STUDY_SKILLS_TIME: "Developing effective study skills like time management",
    DIGITAL_LITERACY: "Skill Guidance/Digital Literacy",
    OTHER: "Any other"
};

// Helper function to get auth headers
const getAuthHeaders = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
        throw new Error("No access token found. Please log in again.");
    }

    return {
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json"
    };
};

// Helper function to handle API responses
const handleApiResponse = async (response) => {
    const responseData = await response.json();

    if (!response.ok) {
        // Handle different error status codes
        if (response.status === 401) {
            throw new Error("Authentication failed. Please log in again.");
        } else if (response.status === 403) {
            throw new Error("Access denied. You don't have permission to perform this action.");
        } else if (response.status === 404) {
            throw new Error("Resource not found. Please check if the assignment exists.");
        } else if (response.status >= 500) {
            throw new Error("Server error. Please try again later.");
        } else {
            throw new Error(responseData.detail || responseData.message || "An error occurred");
        }
    }

    return responseData;
};

const middleApiService = {
    // Get child's submission for an assignment - Matches backend endpoint
    getChildSubmission: async (childId, assignmentId) => {
        try {
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/parent/children/${childId}/assignments/${assignmentId}/submission`,
                {
                    method: "GET",
                    headers: getAuthHeaders()
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error fetching child's submission:", error);
            throw error;
        }
    },

    // Add caregiver observation - Matches backend endpoint and data structure
    addCaregiverObservation: async (childId, assignmentId, formData) => {
        try {
            // Validation
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            // Convert from UI form format to backend expected format
            const payload = {
                // Screen 1: home resources
                resources: Array.isArray(formData.resources) ? formData.resources : [],
                other_resources: formData.other_resources?.trim() || "",

                // Screen 2: understanding of my child
                understanding: {
                    motivated_to_learn: formData.understanding?.motivated_to_learn || ParentResponse.NOT_SURE,
                    follows_schedule: formData.understanding?.follows_schedule || ParentResponse.NOT_SURE,
                    finds_curriculum_difficult: formData.understanding?.finds_curriculum_difficult || ParentResponse.NOT_SURE,
                    making_good_progress: formData.understanding?.making_good_progress || ParentResponse.NOT_SURE
                },

                // Screen 3: school support needs
                school_support_needs: Array.isArray(formData.school_support_needs) ? formData.school_support_needs : [],
                other_school_support: formData.other_school_support?.trim() || "",

                // Screen 4: home support plan
                home_support_plan: formData.home_support_plan?.trim() || ""
            };

            console.log("Sending payload to API:", payload);

            const response = await fetch(
                `${API_BASE_URL}/middle/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-observation`,
                {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            const result = await handleApiResponse(response);
            console.log("API response:", result);

            return result;
        } catch (error) {
            console.error("Error adding caregiver observation:", error);
            throw error;
        }
    },

    // Add self assessment
    addSelfAssessment: async (assignmentId, formData) => {
        try {
            if (!assignmentId) {
                throw new Error("Assignment ID is required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/student/assignments/${assignmentId}/self-assessment`,
                {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(formData)
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error adding self assessment:", error);
            throw error;
        }
    },

    // Add peer assessment
    addPeerAssessment: async (assignmentId, formData) => {
        try {
            if (!assignmentId) {
                throw new Error("Assignment ID is required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/student/assignments/${assignmentId}/peer-assessment`,
                {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(formData)
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error adding peer assessment:", error);
            throw error;
        }
    },

    // Optional: Get all assignments for a child
    getChildAssignments: async (childId) => {
        try {
            if (!childId) {
                throw new Error("Child ID is required");
            }

            const response = await fetch(
                `${API_BASE_URL}/middle/parent/children/${childId}/assignments`,
                {
                    method: "GET",
                    headers: getAuthHeaders()
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error fetching child's assignments:", error);
            throw error;
        }
    },

    // Optional: Update an existing caregiver observation
    updateCaregiverObservation: async (childId, assignmentId, formData) => {
        try {
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            const payload = {
                resources: Array.isArray(formData.resources) ? formData.resources : [],
                other_resources: formData.other_resources?.trim() || "",
                understanding: {
                    motivated_to_learn: formData.understanding?.motivated_to_learn || ParentResponse.NOT_SURE,
                    follows_schedule: formData.understanding?.follows_schedule || ParentResponse.NOT_SURE,
                    finds_curriculum_difficult: formData.understanding?.finds_curriculum_difficult || ParentResponse.NOT_SURE,
                    making_good_progress: formData.understanding?.making_good_progress || ParentResponse.NOT_SURE
                },
                school_support_needs: Array.isArray(formData.school_support_needs) ? formData.school_support_needs : [],
                other_school_support: formData.other_school_support?.trim() || "",
                home_support_plan: formData.home_support_plan?.trim() || ""
            };

            const response = await fetch(
                `${API_BASE_URL}/middle/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-observation`,
                {
                    method: "PUT",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error updating caregiver observation:", error);
            throw error;
        }
    },

    // Export the enum values for use in components
    ParentResponse,
    HomeResource,
    SchoolSupportArea
};

export default middleApiService;